import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Lock,
  Loader2,
  Crown,
  CheckCircle,
  Clock,
  AlertCircle,
  Copy
} from "lucide-react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

export default function Analytics() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.get("/admin/analytics", {
        params: { admin_password: password }
      });
      setAnalytics(response.data);
      setAuthenticated(true);
      toast.success("Analytics loaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-6">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h1 className="text-2xl font-bold mb-2">Analytics Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Admin access required
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                "View Analytics"
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  const offerStatusData = Object.entries(analytics.offers.by_status).map(([key, value]) => ({
    name: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
    value: value
  }));

  const COLORS = ["#FFA500", "#4169E1", "#FF6347", "#32CD32", "#DC143C", "#9370DB"];

  const slotData = [
    { name: "Available", value: analytics.slots.available, color: "#32CD32" },
    { name: "Locked", value: analytics.slots.locked, color: "#FFA500" },
    { name: "Consumed", value: analytics.slots.consumed, color: "#808080" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Platform performance metrics</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-primary">
                  ${analytics.revenue.total_revenue.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Slots + Commissions
                </p>
              </div>
              <DollarSign className="h-10 w-10 text-primary opacity-20" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Slot Revenue</p>
                <p className="text-3xl font-bold text-green-500">
                  ${analytics.revenue.slot_revenue.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.slots.total_purchased} slots × $50
                </p>
              </div>
              <Crown className="h-10 w-10 text-green-500 opacity-20" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Commissions</p>
                <p className="text-3xl font-bold text-blue-500">
                  ${analytics.revenue.commission_revenue.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  20% from completed dates
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-blue-500 opacity-20" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Payouts</p>
                <p className="text-3xl font-bold text-purple-500">
                  ${analytics.revenue.total_payouts.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  80% to women
                </p>
              </div>
              <Users className="h-10 w-10 text-purple-500 opacity-20" />
            </div>
          </Card>
        </div>

        {/* ESCROW SECTION - Highlighted */}
        <Card className="p-6 border-2 border-orange-500/50 bg-orange-500/5">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-6 w-6 text-orange-500" />
            <div>
              <h2 className="text-xl font-bold">Escrow Monitor</h2>
              <p className="text-sm text-muted-foreground">
                Funds held pending confirmation
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total in Escrow</p>
              <p className="text-3xl font-bold text-orange-500">
                ${analytics.escrow.total_amount.toLocaleString()}
              </p>
            </div>
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Pending Confirmations</p>
              <p className="text-3xl font-bold text-orange-500">
                {analytics.escrow.pending_confirmations}
              </p>
            </div>
            <div className="bg-background rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Avg Escrow Amount</p>
              <p className="text-3xl font-bold text-orange-500">
                ${analytics.escrow.pending_confirmations > 0 
                  ? (analytics.escrow.total_amount / analytics.escrow.pending_confirmations).toFixed(2) 
                  : "0.00"}
              </p>
            </div>
          </div>

          {analytics.escrow.details.length > 0 ? (
            <div className="bg-background rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left">Man</th>
                      <th className="px-4 py-3 text-left">Woman</th>
                      <th className="px-4 py-3 text-right">Offer Amount</th>
                      <th className="px-4 py-3 text-right">In Escrow (80%)</th>
                      <th className="px-4 py-3 text-center">Confirmation Code</th>
                      <th className="px-4 py-3 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {analytics.escrow.details.map((escrow, index) => (
                      <tr key={index} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="font-medium">{escrow.man_name}</div>
                          <div className="text-xs text-muted-foreground">{escrow.man_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{escrow.woman_name}</div>
                          <div className="text-xs text-muted-foreground">{escrow.woman_email}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          ${escrow.offer_amount}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-orange-500 font-bold">
                            ${escrow.escrow_amount}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <code className="bg-primary/10 px-3 py-1 rounded font-mono text-lg tracking-wider">
                              {escrow.confirmation_code}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyCode(escrow.confirmation_code)}
                              className="h-8 w-8 p-0"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {new Date(escrow.paid_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No funds in escrow - all confirmations completed</p>
            </div>
          )}
        </Card>

        {/* User & Slot Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Users</p>
                <p className="text-3xl font-bold">{analytics.users.total}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.users.male} men, {analytics.users.female} women
                </p>
              </div>
              <Users className="h-10 w-10 text-muted-foreground opacity-20" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Active Users</p>
                <p className="text-3xl font-bold">{analytics.users.active_users}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Sent/received offers
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-muted-foreground opacity-20" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Offers</p>
                <p className="text-3xl font-bold">{analytics.offers.total}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.offers.conversion_rate}% completed
                </p>
              </div>
              <Clock className="h-10 w-10 text-muted-foreground opacity-20" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Avg Offer</p>
                <p className="text-3xl font-bold">
                  ${analytics.revenue.avg_offer_amount.toFixed(0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Per date offer
                </p>
              </div>
              <DollarSign className="h-10 w-10 text-muted-foreground opacity-20" />
            </div>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Offer Status Breakdown */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Offer Status Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={offerStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {offerStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Slot Status */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Slot Status Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={slotData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {slotData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 text-sm text-muted-foreground">
              Total Slots Purchased: {analytics.slots.total_purchased}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
